
export default function WorkflowArchitecture() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#F97316]/10 text-[#F97316] rounded-full text-sm font-semibold mb-4 border border-[#F97316]/20">
            06 — Architecture
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1E293B] mb-6">
            Workflow Re-Architecture
          </h2>
          <p className="text-[#64748B] text-lg max-w-3xl mx-auto">
            Transforming complex, fragmented processes into streamlined, intuitive workflows that save time and reduce errors.
          </p>
        </div>

        {/* Before vs After */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Before */}
          <div className="bg-gradient-to-br from-[#FEF2F2] to-white rounded-3xl p-8 border border-[#EF4444]/20 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#EF4444]/10 rounded-xl border border-[#EF4444]/20">
                <i className="ri-close-circle-line text-[#EF4444] text-xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1E293B]">Before</h3>
              <span className="px-3 py-1 bg-[#EF4444]/10 text-[#EF4444] text-xs font-semibold rounded-full border border-[#EF4444]/20">Legacy Flow</span>
            </div>

            <div className="space-y-4">
              {[
                'Resident calls property manager',
                'Manager logs request in spreadsheet',
                'Email sent to vendor manually',
                'Vendor responds via phone/email',
                'Manager updates spreadsheet',
                'Resident calls for status update',
                'Work completed, manual invoice processing'
              ].map((step, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#EF4444]/10 rounded-full text-[#EF4444] text-sm font-bold border border-[#EF4444]/20">{index + 1}</div>
                  <div className="flex-1 bg-white rounded-xl p-4 border border-[#E2E8F0] shadow-sm">
                    <p className="text-[#64748B] text-sm">{step}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-[#EF4444]/20 flex items-center justify-between">
              <span className="text-[#64748B] text-sm">Average Time</span>
              <span className="text-[#EF4444] font-bold text-xl">7-14 Days</span>
            </div>
          </div>

          {/* After */}
          <div className="bg-gradient-to-br from-[#F0FDF9] to-white rounded-3xl p-8 border border-[#1FA372]/20 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#1FA372]/10 rounded-xl border border-[#1FA372]/20">
                <i className="ri-check-double-line text-[#1FA372] text-xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1E293B]">After</h3>
              <span className="px-3 py-1 bg-[#1FA372]/10 text-[#1FA372] text-xs font-semibold rounded-full border border-[#1FA372]/20">Optimized Flow</span>
            </div>

            <div className="space-y-4">
              {[
                'Resident submits request via app',
                'Auto-routed to appropriate vendor',
                'Real-time status updates to all parties',
                'Automated payment & completion'
              ].map((step, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#1FA372]/10 rounded-full text-[#1FA372] text-sm font-bold border border-[#1FA372]/20">{index + 1}</div>
                  <div className="flex-1 bg-white rounded-xl p-4 border border-[#E2E8F0] shadow-sm">
                    <p className="text-[#64748B] text-sm">{step}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-[#1FA372]/20 flex items-center justify-between">
              <span className="text-[#64748B] text-sm">Average Time</span>
              <span className="text-[#1FA372] font-bold text-xl">24-48 Hours</span>
            </div>

            <div className="mt-4 bg-[#1FA372]/10 rounded-xl p-4 flex items-center gap-3 border border-[#1FA372]/20">
              <i className="ri-rocket-line text-[#1FA372] text-xl"></i>
              <span className="text-[#1FA372] font-semibold">85% faster resolution time</span>
            </div>
          </div>
        </div>

        {/* Information Architecture Diagram */}
        <div className="bg-[#FAFBFC] rounded-3xl p-8 border border-[#E2E8F0] shadow-sm">
          <h3 className="text-2xl font-bold text-[#1E293B] mb-8 text-center">Information Architecture</h3>
          
          <div className="relative">
            {/* Main Dashboard */}
            <div className="flex justify-center mb-8">
              <div className="bg-[#1FA372] rounded-2xl px-8 py-4 text-white font-bold text-lg shadow-lg">
                Dashboard Home
              </div>
            </div>

            {/* Level 1 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-4 text-center border border-[#1FA372]/30 shadow-sm hover:shadow-md transition-shadow">
                <i className="ri-money-dollar-circle-line text-[#1FA372] text-2xl mb-2"></i>
                <p className="text-[#1E293B] font-medium text-sm">Payments</p>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border border-[#F97316]/30 shadow-sm hover:shadow-md transition-shadow">
                <i className="ri-tools-line text-[#F97316] text-2xl mb-2"></i>
                <p className="text-[#1E293B] font-medium text-sm">Maintenance</p>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border border-[#3B82F6]/30 shadow-sm hover:shadow-md transition-shadow">
                <i className="ri-chat-3-line text-[#3B82F6] text-2xl mb-2"></i>
                <p className="text-[#1E293B] font-medium text-sm">Communications</p>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border border-[#8B5CF6]/30 shadow-sm hover:shadow-md transition-shadow">
                <i className="ri-file-list-3-line text-[#8B5CF6] text-2xl mb-2"></i>
                <p className="text-[#1E293B] font-medium text-sm">Documents</p>
              </div>
            </div>

            {/* Level 2 */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {['Pay Dues', 'History', 'New Request', 'Track Status', 'Announcements', 'CC&Rs'].map((item, index) => (
                <div key={index} className="bg-white rounded-lg p-3 text-center border border-[#E2E8F0] shadow-sm">
                  <p className="text-[#64748B] text-xs">{item}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Wireframe Placeholder */}
          <div className="mt-12 bg-white rounded-2xl p-4 border-2 border-dashed border-[#E2E8F0]">
            <div className="aspect-[16/9] rounded-xl overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=Clean%20wireframe%20mockup%20of%20HOA%20management%20app%20information%20architecture%20showing%20navigation%20structure%20and%20user%20flow%20diagram%20with%20light%20white%20background%20professional%20UX%20design%20documentation%20style%20minimal%20lines%20and%20boxes%20representing%20app%20screens%20and%20connections&width=1200&height=675&seq=hoa-ia-wireframe-light-001&orientation=landscape"
                alt="Information Architecture Wireframe"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <p className="text-[#64748B] text-sm text-center mt-4">Information Architecture & Navigation Flow Diagram</p>
          </div>
        </div>
      </div>
    </section>
  );
}
